(function () {
  if (window.initOptionPerformanceLabels) return;

  const PERF_KEYS = [
    '1080p_low', '1080p_med', '1080p_high', '1080p_ultra',
    '1440p_low', '1440p_med', '1440p_high', '1440p_ultra',
    '4K_low', '4K_med', '4K_high', '4K_ultra'
  ];

  const GROUPS = {
    cpu: {
      type: 'cpu',
      rowAttr: 'data-gm-cpu-option',
      noteClass: 'gm-option-perf-note gm-option-perf-note--cpu',
      summaryAttr: 'data-gm-cpu-summary-note',
      getProfileId: getCpuProfileId,
      loadProfiles: loadCpuProfiles
    },
    gpu: {
      type: 'gpu',
      rowAttr: 'data-gm-gpu-option',
      noteClass: 'gm-option-perf-note gm-option-perf-note--gpu',
      summaryAttr: 'data-gm-gpu-summary-note',
      getProfileId: getGpuProfileId,
      loadProfiles: loadGpuProfiles
    },
    ram: {
      type: 'ram',
      rowAttr: 'data-gm-ram-option',
      noteClass: 'gm-option-perf-note gm-option-perf-note--ram',
      summaryAttr: 'data-gm-ram-summary-note',
      getProfileId: getRamProfileId,
      loadProfiles: loadRamProfiles
    }
  };

  let cpuProfilesPromise = null;
  let gpuProfilesPromise = null;
  let ramProfilesPromise = null;
  let scheduled = false;

  function normalizeText(value) {
    return String(value || '')
      .replace(/\u00d7/g, 'x')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function compactText(value) {
    return normalizeText(value).replace(/\s+/g, '');
  }

  function hasAny(text, patterns) {
    for (let i = 0; i < patterns.length; i += 1) {
      if (text.includes(patterns[i])) return true;
    }
    return false;
  }

  function bestBoost(fromProfile, toProfile) {
    if (!fromProfile || !toProfile) return 0;

    let best = 0;

    for (let i = 0; i < PERF_KEYS.length; i += 1) {
      const key = PERF_KEYS[i];
      const fromValue = Number(fromProfile[key]);
      const toValue = Number(toProfile[key]);

      if (!Number.isFinite(fromValue) || !Number.isFinite(toValue) || fromValue <= 0) continue;

      const uplift = ((toValue / fromValue) - 1) * 100;
      if (uplift > best) best = uplift;
    }

    return Math.max(0, Math.round(best));
  }

  function loadJsonOnce(url, label) {
    if (!url) return Promise.resolve({});

    return fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('Failed to load ' + label);
        return response.json();
      })
      .catch(function () {
        return {};
      });
  }

  function loadCpuProfiles() {
    if (!cpuProfilesPromise) {
      const url = window.GMOptionPerformance && window.GMOptionPerformance.cpuProfilesUrl;
      cpuProfilesPromise = loadJsonOnce(url, 'CPU profiles');
    }
    return cpuProfilesPromise;
  }

  function loadGpuProfiles() {
    if (!gpuProfilesPromise) {
      const url = window.GMOptionPerformance && window.GMOptionPerformance.gpuProfilesUrl;
      gpuProfilesPromise = loadJsonOnce(url, 'GPU profiles');
    }
    return gpuProfilesPromise;
  }

  function loadRamProfiles() {
    if (!ramProfilesPromise) {
      const url = window.GMOptionPerformance && window.GMOptionPerformance.ramProfilesUrl;
      ramProfilesPromise = loadJsonOnce(url, 'RAM profiles');
    }
    return ramProfilesPromise;
  }

  function getCpuProfileId(cpuLabel) {
    const c = normalizeText(cpuLabel);

    if (c.includes('ryzen 5 5500')) return 'r5-5500';
    if (c.includes('ryzen 5 5600')) return 'r5-5600';
    if (c.includes('ryzen 5 7400f') || c.includes('r5 7400f') || c.includes('r5-7400f')) return 'r5-7400f';
    if (c.includes('ryzen 5 7500f') || c.includes('r5 7500f') || c.includes('r5-7500f')) return 'r5-7500f';
    if (c.includes('ryzen 5 8400f') || c.includes('r5 8400f') || c.includes('r5-8400f')) return 'r5-8400f';
    if (c.includes('ryzen 5 9600x') || c.includes('r5 9600x') || c.includes('r5-9600x')) return 'r5-9600x';

    if (c.includes('ryzen 7 5700x')) return 'r7-5700x';
    if (c.includes('ryzen 7 7700')) return 'r7-7700';
    if (c.includes('ryzen 7 7800x3d')) return 'r7-7800x3d';
    if (c.includes('ryzen 7 8700f')) return 'r7-8700f';
    if (c.includes('ryzen 7 9800x3d')) return 'r7-9800x3d';

    if (c.includes('ryzen 9 9900x3d')) return 'r9-9900x3d';

    if (c.includes('i5-14400f') || c.includes('i5 14400f')) return 'i5-14400f';
    if (c.includes('i7-14700f') || c.includes('i7 14700f')) return 'i7-14700f';

    if (c.includes('ultra 5 225f')) return 'ultra-5-225f';
    if (c.includes('ultra 7 265k')) return 'ultra-7-265k';

    return null;
  }

  function getGpuProfileId(gpuLabel) {
    const g = normalizeText(gpuLabel);

    if (g.includes('rtx 3050') && g.includes('6gb')) return 'rtx-3050-6gb';
    if (g.includes('rtx 3050')) return 'rtx-3050-6gb';

    if (g.includes('rtx 5050') && g.includes('8gb')) return 'rtx-5050-8gb';
    if (g.includes('rtx 5050')) return 'rtx-5050-8gb';

    if (g.includes('rtx 5060 ti') && g.includes('16gb')) return 'rtx-5060-ti-16gb';
    if (g.includes('rtx 5060 ti') && g.includes('8gb')) return 'rtx-5060-ti-8gb';
    if (g.includes('rtx 5060 ti')) return 'rtx-5060-ti-8gb';

    if (g.includes('rtx 5060') && g.includes('8gb')) return 'rtx-5060-8gb';
    if (g.includes('rtx 5060')) return 'rtx-5060-8gb';

    if (g.includes('rtx 5070 ti')) return 'rtx-5070-ti';
    if (g.includes('rtx 5070')) return 'rtx-5070';
    if (g.includes('rtx 5080')) return 'rtx-5080';
    if (g.includes('rtx 5090')) return 'rtx-5090';

    if (g.includes('rx 9060 xt') && g.includes('16gb')) return 'rx-9060-xt-16gb';
    if (g.includes('rx 9060 xt') && g.includes('8gb')) return 'rx-9060-xt-8gb';
    if (g.includes('rx 9060 xt')) return 'rx-9060-xt-8gb';
    if (g.includes('rx 9070 xt')) return 'rx-9070-xt';
    if (g.includes('rx 9070')) return 'rx-9070';

    return null;
  }

  function getRamProfileId(ramLabel) {
  const raw = String(ramLabel || '').toLowerCase().trim();

  const isDDR5 = /\bddr\s*5\b|\bd5\b/.test(raw);
  const isDDR4 = /\bddr\s*4\b|\bd4\b/.test(raw);

  const is64 = /\b64\s*gb\b/.test(raw);
  const is32 = /\b32\s*gb\b/.test(raw);
  const is16 = /\b16\s*gb\b/.test(raw);

  const is2x8  = /\b2\s*x\s*8\s*gb\b|\b2\s*x\s*8\b/.test(raw);
  const is2x16 = /\b2\s*x\s*16\s*gb\b|\b2\s*x\s*16\b/.test(raw);
  const is2x32 = /\b2\s*x\s*32\s*gb\b|\b2\s*x\s*32\b/.test(raw);

  const is1x8  = /\b1\s*x\s*8\s*gb\b|\b1\s*x\s*8\b/.test(raw);
  const is1x16 = /\b1\s*x\s*16\s*gb\b|\b1\s*x\s*16\b/.test(raw);

  const isDual = is2x8 || is2x16 || is2x32 || /\bdual\b|\bdual\s*channel\b|\bkit\b/.test(raw);
  const isSingle = is1x8 || is1x16 || /\bsingle\b|\bsingle\s*channel\b/.test(raw);

  if (isDDR5) {
    if (is64 && (is2x32 || isDual)) return '64gb-ddr5-dual';
    if (is32 && (is2x16 || isDual)) return '32gb-ddr5-dual';
    if (is16 && (is2x8 || isDual)) return '16gb-ddr5-dual';
    if (is16 && isSingle) return '16gb-ddr5-single';
  }

  if (isDDR4) {
    if (is64 && (is2x32 || isDual)) return '64gb-ddr4-dual';
    if (is32 && (is2x16 || isDual)) return '32gb-ddr4-dual';
    if (is16 && (is2x8 || isDual)) return '16gb-ddr4-dual';
    if (is16 && isSingle) return '16gb-ddr4-single';
  }

  return null;
}

  function getRows(root, rowAttr) {
    return Array.from(root.querySelectorAll('[' + rowAttr + ']'));
  }

  function getRowLabel(row, rowAttr) {
    return String(row.getAttribute(rowAttr) || '').trim();
  }

  function getCurrentSelectedRow(root, rowAttr) {
    const rows = getRows(root, rowAttr);

    return rows.find(function (row) {
      if (row.classList.contains('is-active')) return true;
      if (row.getAttribute('aria-selected') === 'true') return true;

      const text = normalizeText(row.textContent);
      if (text.includes('included')) return true;

      return false;
    }) || null;
  }

  function getCurrentSelectedLabel(root, group) {
    const selectedRow = getCurrentSelectedRow(root, group.rowAttr);
    if (selectedRow) return getRowLabel(selectedRow, group.rowAttr);
    return '';
  }

  function getBestAnchor(row) {
    return (
      row.querySelector('.gm-card-dropdown__label') ||
      row.querySelector('.gm-card-dropdown__title') ||
      row.querySelector('.gm-card-dropdown__text') ||
      row.querySelector('.gm-card-dropdown__price') ||
      row.firstElementChild ||
      row
    );
  }

  function getNoteElement(row, group) {
    let note = row.querySelector('[data-gm-perf-note="' + group.type + '"]');
    if (note) return note;

    note = document.createElement('div');
    note.className = group.noteClass;
    note.setAttribute('data-gm-perf-note', group.type);

    const anchor = getBestAnchor(row);

    if (anchor === row) {
      row.appendChild(note);
    } else {
      anchor.insertAdjacentElement('afterend', note);
    }

    return note;
  }

  function setNoteText(row, group, text) {
    const note = getNoteElement(row, group);
    note.textContent = text || '';
    note.hidden = !text;
  }

  function getSummaryElement(root, group) {
    return root.querySelector('[' + group.summaryAttr + ']');
  }

  function setSummaryText(root, group, text) {
    const el = getSummaryElement(root, group);
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
  }

  function getBestUpgradeSummary(rows, rowAttr, currentId, profiles, getProfileId) {
    if (!currentId || !profiles[currentId]) return '';

    let best = 0;

    rows.forEach(function (row) {
      const targetId = getProfileId(getRowLabel(row, rowAttr));
      if (!targetId || !profiles[targetId] || targetId === currentId) return;

      const boost = bestBoost(profiles[currentId], profiles[targetId]);
      if (boost > best) best = boost;
    });

    return best > 0 ? 'Up to ' + best + '% more FPS available' : '';
  }

  async function applyGroup(root, group) {
    const rows = getRows(root, group.rowAttr);
    if (!rows.length) return;

    const profiles = await group.loadProfiles();
    const currentLabel = getCurrentSelectedLabel(root, group);
    const currentId = group.getProfileId(currentLabel);

if (group.type === 'ram') {
  console.log('RAM debug:', { currentLabel, currentId, profiles });
}

    if (!currentId || !profiles[currentId]) {
      rows.forEach(function (row) {
        setNoteText(row, group, '');
      });
      setSummaryText(root, group, '');
      return;
    }

    rows.forEach(function (row) {
      const label = getRowLabel(row, group.rowAttr);
      const targetId = group.getProfileId(label);

      if (!targetId || !profiles[targetId] || targetId === currentId) {
        setNoteText(row, group, '');
        return;
      }

      const boost = bestBoost(profiles[currentId], profiles[targetId]);
      setNoteText(row, group, boost > 0 ? 'Up to ' + boost + '% more FPS' : '');
    });

    setSummaryText(
      root,
      group,
      getBestUpgradeSummary(rows, group.rowAttr, currentId, profiles, group.getProfileId)
    );
  }

  async function run(scope) {
    const root = scope || document;

    await Promise.all([
      applyGroup(root, GROUPS.cpu),
      applyGroup(root, GROUPS.gpu),
      applyGroup(root, GROUPS.ram)
    ]);
  }

  function scheduleRun(scope) {
    if (scheduled) return;
    scheduled = true;

    setTimeout(function () {
      scheduled = false;
      run(scope || document);
    }, 0);
  }

  window.initOptionPerformanceLabels = run;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      run(document);
    });
  } else {
    run(document);
  }

  document.addEventListener('click', function (event) {
    const row = event.target && event.target.closest
      ? event.target.closest('[data-gm-cpu-option], [data-gm-gpu-option], [data-gm-ram-option]')
      : null;

    if (!row) return;
    scheduleRun(document);
  });

  document.addEventListener('change', function () {
    scheduleRun(document);
  });

  document.addEventListener('variant:change', function () {
    scheduleRun(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    scheduleRun(event.target || document);
  });
})();